import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { PatternCandles } from '@/entities/pattern-candles';

import type { TimeframeType } from '@/shared/types/timeframe';

import type { PatternLabSchema } from '../types/pattern-lab.schema';
import type { PatternStudy } from '../types/pattern-study';

export const initialState: PatternLabSchema = {
    studiedPatterns: [],
    isLoadingCandles: false,
    loadedCandlesCache: [],
    mode: 'comparison',
};

export const patternLabSlice = createSlice({
    name: 'patternLab',
    initialState,
    reducers: {
        setStudiedPatterns: (state, action: PayloadAction<PatternStudy[]>) => {
            state.studiedPatterns = action.payload;
        },
        addStudiedPattern: (state, action: PayloadAction<PatternStudy>) => {
            if (state.studiedPatterns.length < 3) {
                const existingPattern = state.studiedPatterns.find(
                    (pattern) =>
                        pattern.pattern.unique_id ===
                        action.payload.pattern.unique_id,
                );

                if (!existingPattern) {
                    state.studiedPatterns.push(action.payload);
                }
            }
        },
        removeStudiedPattern: (state, action: PayloadAction<string>) => {
            state.studiedPatterns = state.studiedPatterns.filter(
                (pattern) => pattern.pattern.unique_id !== action.payload,
            );
            state.loadedCandlesCache = state.loadedCandlesCache.filter(
                (value) => action.payload !== value,
            );
        },
        setPatternCandles: (
            state,
            action: PayloadAction<{
                patternId: string;
                candles: PatternCandles;
                timeframe: TimeframeType;
            }>,
        ) => {
            const { patternId, candles, timeframe } = action.payload;
            const currentPattern = state.studiedPatterns.find(
                (pattern) => pattern.pattern.unique_id === patternId,
            );

            if (currentPattern) {
                // Add timeframe to PatternCandles directly
                const candlesWithTimeframe: PatternCandles = {
                    ...candles,
                    timeframe,
                };

                // Update pattern candles
                state.studiedPatterns = state.studiedPatterns.map((pattern) =>
                    pattern.pattern.unique_id === patternId
                        ? {
                              ...pattern,
                              candles: candlesWithTimeframe,
                          }
                        : pattern,
                );
            }
        },
        setLoadingCandles: (state, action: PayloadAction<boolean>) => {
            state.isLoadingCandles = action.payload;
        },
        updateStudiedPatternColor: (
            state,
            action: PayloadAction<{
                color: string | undefined;
                patternUniqueId?: string;
            }>,
        ) => {
            const { color, patternUniqueId } = action.payload;

            if (patternUniqueId) {
                state.studiedPatterns = state.studiedPatterns.map(
                    (patternStudy) =>
                        patternStudy.pattern.unique_id === patternUniqueId
                            ? { ...patternStudy, color }
                            : patternStudy,
                );
            }
        },
        updateStudiedPatternName: (
            state,
            action: PayloadAction<{ name: string; patternUniqueId: string }>,
        ) => {
            const { name, patternUniqueId } = action.payload;

            state.studiedPatterns = state.studiedPatterns.map((patternStudy) =>
                patternStudy.pattern.unique_id === patternUniqueId
                    ? { ...patternStudy, name }
                    : patternStudy,
            );
        },
        setMode: (
            state,
            action: PayloadAction<'comparison' | 'aggregation'>,
        ) => {
            state.mode = action.payload;
        },
    },
});

export const {
    setStudiedPatterns,
    addStudiedPattern,
    removeStudiedPattern,
    setPatternCandles,
    setLoadingCandles,
    updateStudiedPatternColor,
    updateStudiedPatternName,
    setMode,
} = patternLabSlice.actions;

export const patternLabReducer = patternLabSlice.reducer;
